<script setup>
  import { ref, defineProps, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
  import { STATS, prevStat, nextStat } from './constants.js';


  const props = defineProps({
    stats: Object,
    padding: Number,
    bstLocked: Boolean,
    adjustAllStats: Function,
    setSingleStat: Function,
  })

  const baseSize = 640;

  const statAngles = {
    'hp': 0,
    'atk': 60,
    'def': 120,
    'speed': 180,
    'spatk': 240,
    'spdef': 300
  };

  //TODO: make the 75/50 lines actually represent numbers, like every 50 points or something.
  // then they can scale with the top end with statDisplayMultiplier
  var linesAtPoints = [50, 75, 100, 120, 180, 255]


  const diagram = ref(null)

  const svgPoints = reactive([]);
  const radialLines = reactive([]);
  const refLines = reactive([]);

  const statPolys = reactive([]);
  const statLines = reactive([]);
  const statHandles = reactive([]);

  const mouseIsDown = ref(false)
  const statMouseIsChanging = ref(null)


  onMounted(() => {
    updatePoints();

    document.querySelector('body').addEventListener('mousemove', onMouseMove);
    document.querySelector('body').addEventListener('mouseup', onMouseUp);
  })
  onUnmounted(()=>{
    document.querySelector('body').removeEventListener('mousemove', onMouseMove);
    document.querySelector('body').removeEventListener('mouseup', onMouseUp);
  })


  const viewBox = computed(()=>{
    return '0 0 '+baseSize +' '+ baseSize;
  })
  const maxStat = computed(()=>{
    let componentStats = {...props.stats};
    delete componentStats['bst']
    return Math.max(...Object.values(componentStats), (720 / 6));
    // return  Math.max(...Object.values(stats), (720 / 6));//120
  })
  const statDisplayMultiplier = computed(()=>{
    return 1 / maxStat.value;
  })

  const padSize = computed(()=>{
    return baseSize - props.padding * 2;
  })
  const centre = computed(()=>{
    return baseSize / 2;
  })
  const radius = computed(()=>{
    return padSize.value / 2;
  })






  const radians = (degrees) => {
    return degrees * (Math.PI / 180.0);
  }

  const rotate = (x, y, degrees) => {
    var angle = radians(degrees);
    var s = Math.sin(angle);
    var c = Math.cos(angle);
    return [
      x * c - y * s,
      x * s + y * c
    ];
  }

  const translate = (vec_point, x, y) => {
    return [vec_point[0] + x, vec_point[1] + y];
  }


  const halfway = (v1, v2) => {
    let y1 = v1[1];
    let x1 = v1[0];
    let x2 = v2[0];
    let y2 = v2[1];
    return [
      (Math.max(x1, x2) - Math.min(x1, x2)) / 2 + Math.min(x1, x2),
      (Math.max(y1, y2) - Math.min(y1, y2)) / 2 + Math.min(y1, y2),
    ];
  }

  const updatePoints = () => {

    svgPoints['outer'] = "";
    svgPoints['other'] =  {};

    for (let l in linesAtPoints) {
      if (linesAtPoints[l] < maxStat.value) {
        svgPoints['other'][linesAtPoints[l]] = "";
      }
    }

    radialLines.splice(0);

    for (let angle = 0; angle < 360; angle += 60) {
      let outerpoint = translate(rotate(0, -radius.value, angle), centre.value, centre.value);

      svgPoints['outer'] += " " + outerpoint.join(',');

      for (let l in svgPoints['other']) {
        svgPoints['other'][l] += " " + translate(
          rotate(0, -radius.value * statDisplayMultiplier.value * l, angle),
          centre.value,
          centre.value
        ).join(',');
      }

      radialLines.push({
        angle,
        outerpoint
      })
    }



    //lines at important intervals
    refLines.splice(0);
    for (let l in svgPoints['other']) {
      refLines.push({
        l,
        points: svgPoints['other'][l]
      })
    }






    //calculate displayed positions
    let statPositions = {};
    for (let s in STATS) {
      let rotated = rotate(0, -props.stats[STATS[s]] * statDisplayMultiplier.value * radius.value, statAngles[STATS[s]]);
      statPositions[STATS[s]] = [rotated[0] + centre.value, rotated[1] + centre.value];
    }


    //then the point lists including halfway points between them
    var pointLists = {};
    for (let s in STATS) {
      pointLists[STATS[s]] = [
        [centre.value, centre.value].join(","),
        halfway(statPositions[prevStat(STATS[s])], statPositions[STATS[s]]).join(","),
        statPositions[STATS[s]].join(","),
        halfway(statPositions[nextStat(STATS[s])], statPositions[STATS[s]]).join(","),
      ];
    }


    //back to drawing
    //shaped polygons for the area
    //outer line edges for each area
    ///and handles for moving
    statPolys.splice(0);
    statLines.splice(0);
    statHandles.splice(0);
    for (let s in STATS) {
      statPolys.push({
        'stat': STATS[s],
        'points': pointLists[STATS[s]].join(" ")
      });

      statLines.push({
        'stat': STATS[s],
        'points': pointLists[STATS[s]].slice(1).join(" ")
      });

      statHandles.push({
        'stat': STATS[s],
        'position': statPositions[STATS[s]]
      })

    }
    //onTouchStart={props.statMouseDown}
  }

  const onMouseDown = (stat)=>{
    mouseIsDown.value = true
    statMouseIsChanging.value = stat
  }
  const onMouseMove = (e)=>{
    if (mouseIsDown.value){
      let svgBounds = diagram.value.getBoundingClientRect();
      let svgCentre = [svgBounds.left + svgBounds.width / 2, svgBounds.top + svgBounds.height / 2];

      let relativeMousePos = [e.clientX - svgCentre[0], e.clientY - svgCentre[1]]
      let distanceFromCentre = Math.sqrt(relativeMousePos[0] * relativeMousePos[0] + relativeMousePos[1] * relativeMousePos[1])

      let normalised = distanceFromCentre / (svgBounds.width / 2 - props.padding);





      // console.log(relativeMousePos, distanceFromCentre, normalised, maxStat*normalised)
      if (!props.bstLocked && e.shiftKey) {
        var diff = maxStat.value * normalised - props.stats[statMouseIsChanging.value];
        props.adjustAllStats(diff);

      } else {

        props.setSingleStat(statMouseIsChanging.value, maxStat.value * normalised);
      }
    }
  }

  const onMouseUp = ()=>{
    mouseIsDown.value = false;
    statMouseIsChanging.value = null;
  }



  watch(
    props.stats,
    ()=>updatePoints(),
    {deep: true}
  )

</script>

<template>
  <svg
    ref="diagram"
    class="stat-distribution select-none max-w-full"
    :viewBox="viewBox"
    @mousemove="onMouseMove"
    @mouseup="onMouseUp"
  >

    <polygon
      class="bg-line stroke-2 stroke-gray-500 fill-transparent"
      :points="svgPoints['outer']"
    />

    <polygon
      v-for="line in refLines"
      :key="line.l"
      class="bg-line stroke-1 stroke-gray-500 fill-transparent"
      :points="line.points"
    />

    <line
      v-for="line in radialLines"
      :key="line.angle"
      class="bg-line stroke-1 stroke-gray-500 fill-transparent"
      :x1="centre"
      :y1="centre"
      :x2="line.outerpoint[0]"
      :y2="line.outerpoint[1]"
    />

    <polygon
      v-for="(poly, p) in statPolys"
      :key="'poly-' + p"
      :class="'stat-poly opacity-50 fill-stat-' + poly.stat"
      :points="poly.points"
    />

    <polyline
      v-for="(poly, p) in statLines"
      :key="'line-' + p"
      :class="'stat-edge stroke-2 stroke-stat-' + poly.stat+' fill-transparent'"
      :points="poly.points"
    />

    <circle
      v-for="(handle, h) in statHandles"
      :key="'handle-'+h"
      r="12"
      :class="'stat-handle stroke-2  stroke-stat-'+ handle.stat+' fill-white shadow-lg cursor-pointer'"
      :cx="handle.position[0]"
      :cy="handle.position[1]"
      style="{
        'transformOrigin': `${handle.position[0]}px ${handle.position[1]}px`
      }"
      @mousedown="onMouseDown(handle.stat)"
    />
  </svg>
</template>
